import { useEffect, useRef } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import SyncAltIcon from '@mui/icons-material/SyncAlt'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import { fsColors } from '../../../shared/constants'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { changeEtlFileMappings, getFileMappings, setEtlFiles } from '../../../store/actions'
import { etlAPI } from '../../../shared/api/ETLAPI'
import { useLoading } from '../../../shared/hooks'
import ACTIONS from '../../../store/constants'
import FsAlert, { FsAlertRef } from '../../common/atoms/Alert'

const FsFileMappings = ({
  setIsLoading,
  isInternalUser = true,
  isReadOnly = false,
}: {
  setIsLoading: (isLoading: boolean) => void
  isInternalUser?: boolean
  isReadOnly?: boolean
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useAppDispatch()
  const { etlFiles, latestEtlStatus, etlFileMappings } = useAppSelector((state) => state.currentEtl)
  const etlFileNames = etlFiles ? Object.keys(etlFiles) : []

  const alertRef = useRef<FsAlertRef>(null)

  useEffect(() => {
    if (latestEtlStatus) {
      dispatch(setEtlFiles({ runId: latestEtlStatus.id }))
      dispatch(getFileMappings())
    }
  }, [latestEtlStatus])

  const { isFetching } = useLoading([ACTIONS.ETL.SET_ETL_FILES, ACTIONS.ETL.GET_FILE_MAPPINGS])

  useEffect(() => {
    setIsLoading(isFetching)
  }, [isFetching])

  const deleteFile = async (fileName: string) => {
    if (latestEtlStatus) {
      try {
        setIsLoading(true)
        const deleteStatus = await etlAPI.deleteFile(fileName, latestEtlStatus.id)
        if (deleteStatus === 200) {
          dispatch(setEtlFiles({ runId: latestEtlStatus.id }))
          dispatch(getFileMappings())
          alertRef.current?.openAlert({ message: t('file.deleted') })
        } else {
          alertRef.current?.openAlert({ severity: 'error', message: t('file.deleted.error') })
        }
      } catch {
        alertRef.current?.openAlert({ severity: 'error', message: t('file.deleted.error') })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const downloadFile = async (fileName: string) => {
    if (latestEtlStatus) {
      try {
        setIsLoading(true)
        const downloadURL: any = await etlAPI.downloadFile(fileName, latestEtlStatus.id)
        if (downloadURL && downloadURL.url) {
          const response = await axios({
            url: downloadURL.url,
            method: 'GET',
            responseType: 'blob',
            timeout: 180000,
          })
          if (response) {
            const blobUrl = URL.createObjectURL(response.data)
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = fileName
            document.body.appendChild(link)
            link.dispatchEvent(
              new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window,
              }),
            )
            setTimeout(() => {
              document.body.removeChild(link)
            }, 100)
          }
        } else {
          alertRef.current?.openAlert({
            severity: 'error',
            message: t('file.download.fail'),
          })
        }
      } catch {
        alertRef.current?.openAlert({ severity: 'error', message: t('file.download.fail') })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const downloadAll = () => {
    if (etlFileNames && etlFileNames.length > 0) {
      etlFileNames.forEach((file) => downloadFile(file))
    }
  }

  return (
    <Box>
      {etlFileNames.length > 0 && (
        <Box my={2}>
          <Typography variant="h6">
            {t('files.uploaded', { number: etlFileNames.length })}
          </Typography>
          {etlFileNames.map((fileName, fIndex) => (
            <Stack
              key={`filename_${fIndex}`}
              my={1}
              py={1}
              pl={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ backgroundColor: theme.palette.action.disabledBackground, borderRadius: 2 }}
            >
              <Typography>{fileName}</Typography>
              {isInternalUser ? (
                <IconButton onClick={() => downloadFile(fileName)}>
                  <DownloadIcon />
                </IconButton>
              ) : (
                <IconButton onClick={() => deleteFile(fileName)}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Stack>
          ))}
          {isInternalUser && etlFileNames.length > 1 && (
            <Button
              variant="outlined"
              sx={{ mt: 1 }}
              startIcon={<DownloadIcon />}
              onClick={downloadAll}
            >
              {t('download.all')}
            </Button>
          )}
        </Box>
      )}
      {etlFiles && etlFileNames.length > 1 && (
        <Box mt={3} mb={2}>
          <Typography variant="h6">{t('file.column.mapping')}</Typography>
          <Typography mb={2}>{t('file.column.mapping.info')}</Typography>
          {etlFileNames.length === 2 ? (
            <Stack direction="row" gap={3} alignItems="center">
              <Stack
                p={1}
                direction="row"
                alignItems="center"
                borderRadius={2}
                sx={{
                  backgroundColor: theme.palette.action.disabledBackground,
                }}
              >
                <Typography variant="body1" mr={5} width={145}>
                  {etlFileNames[0]}
                </Typography>
                <Autocomplete
                  renderInput={(params) => <TextField {...params} label={t('select.column')} />}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    width: 260,
                  }}
                  freeSolo
                  value={
                    etlFileMappings && etlFileMappings[etlFileNames[0]]
                      ? etlFileMappings[etlFileNames[0]].primaryKey ?? null
                      : null
                  }
                  onChange={(_, newValue: string | null) => {
                    dispatch(
                      changeEtlFileMappings({
                        ...etlFileMappings,
                        [etlFileNames[0]]: {
                          ...etlFileMappings[etlFileNames[0]],
                          primaryKey: newValue ?? null,
                        },
                        [etlFileNames[1]]: {
                          ...etlFileMappings[etlFileNames[1]],
                          foreignKey: newValue ?? null,
                        },
                      }),
                    )
                  }}
                  options={etlFiles[etlFileNames[0]]}
                  disabled={isReadOnly}
                />
              </Stack>
              <SyncAltIcon sx={{ color: fsColors.fsGray.colors[1] }} />
              <Stack
                p={1}
                direction="row"
                alignItems="center"
                borderRadius={2}
                sx={{
                  backgroundColor: theme.palette.action.disabledBackground,
                }}
              >
                <Typography variant="body1" mr={5} width={145}>
                  {etlFileNames[1]}
                </Typography>
                <Autocomplete
                  renderInput={(params) => <TextField {...params} label={t('select.column')} />}
                  size="small"
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    width: 260,
                  }}
                  freeSolo
                  value={
                    etlFileMappings && etlFileMappings[etlFileNames[0]]
                      ? etlFileMappings[etlFileNames[0]].foreignKey ?? null
                      : null
                  }
                  onChange={(_, newValue: string | null) => {
                    dispatch(
                      changeEtlFileMappings({
                        ...etlFileMappings,
                        [etlFileNames[1]]: {
                          ...etlFileMappings[etlFileNames[1]],
                          primaryKey: newValue ?? null,
                        },
                        [etlFileNames[0]]: {
                          ...etlFileMappings[etlFileNames[0]],
                          foreignKey: newValue ?? null,
                        },
                      }),
                    )
                  }}
                  options={etlFiles[etlFileNames[1]]}
                  disabled={isReadOnly}
                />
              </Stack>
            </Stack>
          ) : (
            etlFileNames.map((file, fmIndex) => (
              <Stack
                key={`filermapping_${fmIndex}`}
                direction="row"
                gap={1}
                alignItems="center"
                mb={2}
              >
                <Stack
                  p={1}
                  direction="row"
                  alignItems="center"
                  borderRadius={2}
                  sx={{
                    backgroundColor: theme.palette.action.disabledBackground,
                  }}
                >
                  <Typography mr={5} width={145}>
                    {file}
                  </Typography>
                  <Autocomplete
                    renderInput={(params) => <TextField {...params} label={t('select.column')} />}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      width: 260,
                    }}
                    freeSolo
                    value={
                      etlFileMappings && etlFileMappings[file]
                        ? etlFileMappings[file].primaryKey ?? null
                        : null
                    }
                    onChange={(_, newValue: string | null) => {
                      dispatch(
                        changeEtlFileMappings({
                          ...etlFileMappings,
                          [file]: {
                            ...etlFileMappings[file],
                            primaryKey: newValue ?? null,
                          },
                        }),
                      )
                    }}
                    options={etlFiles[file]}
                    disabled={isReadOnly}
                  />
                </Stack>
                <SyncAltIcon sx={{ color: fsColors.fsGray.colors[1] }} />
                <Stack
                  p={1}
                  direction="row"
                  alignItems="center"
                  borderRadius={2}
                  gap={2}
                  width={500}
                  sx={{
                    backgroundColor: theme.palette.action.disabledBackground,
                  }}
                >
                  <Autocomplete
                    renderInput={(params) => <TextField {...params} label={t('select.filename')} />}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      width: 260,
                    }}
                    freeSolo
                    value={
                      etlFileMappings && etlFileMappings[file]
                        ? etlFileMappings[file].referenceFile ?? null
                        : null
                    }
                    onChange={(_, newValue: string | null) => {
                      dispatch(
                        changeEtlFileMappings({
                          ...etlFileMappings,
                          [file]: {
                            ...etlFileMappings[file],
                            referenceFile: newValue ?? null,
                          },
                        }),
                      )
                    }}
                    options={etlFileNames.filter((f) => f !== file)}
                    disabled={isReadOnly}
                  />
                  <Autocomplete
                    renderInput={(params) => <TextField {...params} label={t('select.column')} />}
                    size="small"
                    sx={{
                      backgroundColor: theme.palette.background.paper,
                      width: 260,
                    }}
                    freeSolo
                    value={
                      etlFileMappings && etlFileMappings[file]
                        ? etlFileMappings[file].foreignKey ?? null
                        : null
                    }
                    onChange={(_, newValue: string | null) => {
                      dispatch(
                        changeEtlFileMappings({
                          ...etlFileMappings,
                          [file]: {
                            ...etlFileMappings[file],
                            foreignKey: newValue ?? null,
                          },
                        }),
                      )
                    }}
                    disabled={
                      !(
                        etlFileMappings &&
                        etlFileMappings[file] &&
                        etlFileMappings[file].referenceFile
                      ) || isReadOnly
                    }
                    options={
                      etlFileMappings &&
                      etlFileMappings[file] &&
                      etlFileMappings[file].referenceFile
                        ? etlFiles[etlFileMappings[file].referenceFile as string]
                        : []
                    }
                  />
                </Stack>
              </Stack>
            ))
          )}
        </Box>
      )}
      <FsAlert ref={alertRef} />
    </Box>
  )
}

export default FsFileMappings
